import {
  type JSX,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Breadcrumbs as MuiBreadcrumbs,
} from "@mui/material";
import { MoreHorizontalIcon } from "../../../icons/more-horizontal.icon";
import { TextLinkComponent } from "../text-link/text-link.component";
import type {
  BreadcrumbsMenuProps,
  BreadcrumbsProps,
} from "./breadcrumbs.types";
import {
  DEFAULT_MAX_ITEMS,
  DEFAULT_ITEMS_AFTER_COLLAPSE,
  DEFAULT_ITEMS_BEFORE_COLLAPSE,
  BreadcrumbsTheme,
} from "./breadcrumbs.constants";
import styles from "./breadcrumbs.styles.module.scss";
import classNames from "classnames";

const renderItemsBeforeAndAfter = (
  allItems: JSX.Element[],
  separatorItem: JSX.Element,
  itemsBeforeCollapse: number,
  itemsAfterCollapse: number
) => [
  ...allItems.slice(0, itemsBeforeCollapse),
  separatorItem,
  ...allItems.slice(allItems.length - itemsAfterCollapse, allItems.length),
];

const BreadcrumbsMenu = ({
  anchorEl,
  handleClose,
  links,
  itemsAfterCollapse,
  itemsBeforeCollapse,
}: BreadcrumbsMenuProps) => {
  const children = useMemo(
    () =>
      links.reduce<JSX.Element[]>((menuItems, link, i) => {
        const isMenuItem =
          i >= itemsBeforeCollapse &&
          i <= links.length - 1 - itemsAfterCollapse;

        if (isMenuItem) {
          menuItems.push(
            <MenuItem
              key={link.href}
              onClick={(e) => {
                handleClose();
                link.onClick?.(e);
              }}
            >
              {link.label}
            </MenuItem>
          );
        }

        return menuItems;
      }, []),
    [handleClose, itemsAfterCollapse, itemsBeforeCollapse, links]
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      aria-labelledby="rest-breadcrumbs-menu"
      classes={{ list: styles.menuList }}
    >
      {children}
    </Menu>
  );
};

export const Breadcrumbs = ({
  links,
  hideRightBlur,
  maxItems = DEFAULT_MAX_ITEMS,
  itemsAfterCollapse = DEFAULT_ITEMS_AFTER_COLLAPSE,
  itemsBeforeCollapse = DEFAULT_ITEMS_BEFORE_COLLAPSE,
  theme = BreadcrumbsTheme.LIGHT,
}: BreadcrumbsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const shouldHideLinks = links.length > maxItems;

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement> | null) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const allItems = useMemo(() =>
    links.map(({ href, label, onClick }, i) => {
      const isLast = i === links.length - 1;

      if (isLast) {
        return (
          <span key={href} className={styles.last}>
            {label}
          </span>
        );
      }

      return (
        <TextLinkComponent
          key={href}
          onClick={onClick}
          text={label}
          href={href}
          variant="prominent"
          theme={theme}
        />
      );
    }), [links, theme]);

  const children = useMemo(() => {
    if (shouldHideLinks) {
      return renderItemsBeforeAndAfter(
        allItems,
        <IconButton
          key="more-icon-btn"
          data-testid="more-icon-btn"
          color="primary"
          size="small"
          onClick={handleClick}
          classes={{ root: styles.iconBtnRoot }}
        >
          <MoreHorizontalIcon classes={{ root: styles.iconRoot }} />
        </IconButton>,
        itemsBeforeCollapse,
        itemsAfterCollapse
      );
    }

    return allItems;
  }, [
    allItems,
    handleClick,
    itemsAfterCollapse,
    itemsBeforeCollapse,
    shouldHideLinks,
  ]);

  return (
    <>
      {shouldHideLinks && (
        <BreadcrumbsMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          itemsAfterCollapse={itemsAfterCollapse}
          itemsBeforeCollapse={itemsBeforeCollapse}
          links={links}
        />
      )}
      <div className={styles.container}>
        <MuiBreadcrumbs
          classes={{
            root: classNames({
              [styles.root]: true,
              [styles.breadcrumbsThemeLight]: BreadcrumbsTheme.LIGHT === theme,
              [styles.breadcrumbsThemeDark]: BreadcrumbsTheme.DARK === theme,
            }),
            ol: styles.ol,
            li: styles.li,
            separator: styles.separator,
          }}
          aria-label="breadcrumb"
        >
          {children}
        </MuiBreadcrumbs>
        {!hideRightBlur && <div data-testid="rectangle-div" className={styles.rectangle} />}
      </div>
    </>
  );
};
